import { createContext, useContext, useReducer, useRef, memo } from "react";
import ReactDOM from "react-dom";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import PropTypes from "prop-types";

const AlertContext = createContext();

const initialState = { open: false, message: "", severity: "success", duration: 6000 };

const alertReducer = (state, action) => {
  switch (action.type) {
    case "SHOW":
      return { open: true, message: action.message, severity: action.severity, duration: action.duration };
    case "HIDE":
      return { ...state, open: false };
    default:
      return state;
  }
};

// Snackbar lives in its own isolated component — its state changes never cause children to re-render
const AlertSnackbar = ({ dispatchRef }) => {
  const [state, dispatch] = useReducer(alertReducer, initialState);

  // Expose dispatch via ref so AlertProvider can trigger it without re-rendering children
  dispatchRef.current = dispatch;

  const handleClose = () => dispatch({ type: "HIDE" });

  return ReactDOM.createPortal(
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={state.open}
      autoHideDuration={state.duration}
      onClose={handleClose}
    >
      <Alert onClose={handleClose} severity={state.severity} sx={{ width: "100%" }}>
        {state.message}
      </Alert>
    </Snackbar>,
    document.body
  );
};

AlertSnackbar.propTypes = {
  dispatchRef: PropTypes.object,
};

// memo ensures AlertSnackbar never re-renders due to AlertProvider re-renders
const MemoSnackbar = memo(AlertSnackbar);

export const AlertProvider = ({ children }) => {
  const dispatchRef = useRef(null);

  const showAlert = (msg, severityValue = "success", customDuration = 6000) => {
    dispatchRef.current?.({ type: "SHOW", message: msg, severity: severityValue, duration: customDuration });
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <MemoSnackbar dispatchRef={dispatchRef} />
    </AlertContext.Provider>
  );
};

AlertProvider.propTypes = {
  children: PropTypes.node,
};

export const useAlert = () => useContext(AlertContext);
